import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TreeModule } from 'primeng/tree';

import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { EstruturaResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Modal de seleção de estrutura organizacional em formato de árvore.
 *
 * A hierarquia é montada a partir da lista plana de estruturas usando o
 * `idEoSuperior` como referência de pai. A seleção só destaca o nó; a
 * confirmação ocorre apenas ao clicar em "Selecionar".
 *
 * Uso:
 *   <app-seleciona-estrutura #seletor (estruturaSelecionada)="onEstrutura($event)" />
 *   // no componente: this.seletor.abrir();
 */
@Component({
    selector: 'app-seleciona-estrutura',
    standalone: true,
    imports: [DialogoPadraoDirective, ButtonModule, DialogModule, TreeModule],
    templateUrl: './seleciona-estrutura.html',
    styleUrl: './seleciona-estrutura.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelecionaEstrutura {
    private readonly estruturaService = inject(EstruturaService);
    private readonly destroyRef = inject(DestroyRef);
    private estruturasCache: EstruturaResponse[] | null = null;

    readonly header = input('Estruturas');
    readonly estruturaSelecionada = output<EstruturaResponse>();

    protected readonly visivel = signal(false);
    protected readonly buscando = signal(false);
    protected readonly estruturas = signal<EstruturaResponse[]>([]);
    protected readonly selecionado = signal<TreeNode | null>(null);
    protected readonly estruturaTree = computed<TreeNode[]>(() => this.buildTree(this.estruturas()));

    abrir(): void {
        this.selecionado.set(null);
        this.visivel.set(true);
        if (!this.estruturasCache) {
            this.carregar();
        }
    }

    protected fechar(): void {
        this.visivel.set(false);
    }

    /** Confirma a estrutura destacada; a seleção só é emitida ao clicar em "Selecionar". */
    protected confirmarSelecao(): void {
        const estrutura = this.selecionado()?.data as EstruturaResponse | undefined;
        if (!estrutura) {
            return;
        }
        this.estruturaSelecionada.emit(estrutura);
        this.visivel.set(false);
    }

    private carregar(): void {
        this.buscando.set(true);
        this.estruturaService
            .listar()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.buscando.set(false))
            )
            .subscribe({
                next: (estruturas) => {
                    this.estruturasCache = estruturas;
                    this.estruturas.set(estruturas ?? []);
                },
                error: () => this.estruturas.set([])
            });
    }

    private buildTree(estruturas: EstruturaResponse[]): TreeNode[] {
        const map = new Map<number, TreeNode>();
        const roots: TreeNode[] = [];

        for (const estrutura of estruturas) {
            map.set(estrutura.id, {
                key: String(estrutura.id),
                label: `${estrutura.id} ${estrutura.sigla ?? ''} ${estrutura.descricao}`,
                data: estrutura,
                children: [],
                expanded: estrutura.idEoSuperior == null
            });
        }

        for (const estrutura of estruturas) {
            const node = map.get(estrutura.id)!;
            const paiId = estrutura.idEoSuperior;
            const temPaiValido = paiId != null && paiId !== estrutura.id && map.has(paiId);
            if (temPaiValido) {
                map.get(paiId)!.children!.push(node);
            } else {
                roots.push(node);
            }
        }

        const visitados = new Set<string>();
        const markLeaves = (nodes: TreeNode[]): void => {
            for (const node of nodes) {
                if (visitados.has(node.key!)) {
                    continue;
                }
                visitados.add(node.key!);
                if (!node.children || node.children.length === 0) {
                    delete node.children;
                    node.leaf = true;
                    node.icon = 'pi pi-building';
                } else {
                    node.icon = 'pi pi-sitemap';
                    markLeaves(node.children);
                }
            }
        };
        markLeaves(roots);

        return roots;
    }
}
